import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { LandingBlock, LandingBlockType } from '@/types'
import { BLOCK_META, BLOCK_LABELS, makeBlock } from './blockDefaults'
import { BlockEditor } from './BlockEditor'

interface BlockListEditorProps {
  blocks: LandingBlock[]
  onChange: (blocks: LandingBlock[]) => void
  openBlock: string | null
  setOpenBlock: (id: string | null) => void
}

/**
 * Editable, reorderable list of blocks operating on a single block array.
 * Reused for both the control content and each A/B variant.
 */
export const BlockListEditor = ({
  blocks,
  onChange,
  openBlock,
  setOpenBlock,
}: BlockListEditorProps) => {
  const addBlock = (type: LandingBlockType) => {
    const b = makeBlock(type)
    onChange([...blocks, b])
    setOpenBlock(b.id)
  }
  const updateBlock = (block: LandingBlock) =>
    onChange(blocks.map((b) => (b.id === block.id ? block : b)))
  const removeBlock = (id: string) => onChange(blocks.filter((b) => b.id !== id))
  const moveBlock = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= blocks.length) return
    const next = [...blocks]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }
  const toggleVisible = (id: string) =>
    onChange(blocks.map((b) => (b.id === id ? { ...b, visible: b.visible === false } : b)))

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          Aún no hay bloques. Añade el primero abajo.
        </p>
      )}

      {blocks.map((block, i) => {
        const isOpen = openBlock === block.id
        return (
          <div key={block.id} className="rounded-lg border">
            <div className="flex items-center gap-1 p-2">
              <button
                className="flex-1 text-left text-sm font-medium"
                onClick={() => setOpenBlock(isOpen ? null : block.id)}
              >
                {BLOCK_LABELS[block.type] ?? block.type}
                {block.visible === false && (
                  <span className="ml-2 text-xs text-muted-foreground">(oculto)</span>
                )}
              </button>
              <Button variant="ghost" size="sm" onClick={() => moveBlock(i, -1)} disabled={i === 0}>
                <ChevronUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveBlock(i, 1)}
                disabled={i === blocks.length - 1}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => toggleVisible(block.id)}>
                {block.visible === false ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => removeBlock(block.id)}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </div>
            {isOpen && (
              <div className="border-t p-3">
                <BlockEditor block={block} onChange={updateBlock} />
              </div>
            )}
          </div>
        )
      })}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full">
            <Plus className="mr-1 h-4 w-4" />
            Añadir bloque
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72">
          {BLOCK_META.map((b) => (
            <DropdownMenuItem key={b.type} onClick={() => addBlock(b.type)}>
              <div>
                <div className="font-medium">{b.label}</div>
                <div className="text-xs text-muted-foreground">{b.description}</div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
